import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Document,
  Policy,
  PolicyExpedient,
  PolicyReceipt,
  PolicySinister
} from '../../models/policy';
import { PolicyAccident } from '../../models/policy-accident';
import { PendingReceipt } from '../../models/receipt';
import { BaseService } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class AccidentPoliciesService {
  constructor(
    private base: BaseService,
    private http: HttpClient,
    private mmobileHelper: MMobileHelperService,
    private or: OperationRegisterService
  ) {}

  getAccidentPolicy({
    policyNumber,
    type
  }: PolicyAccident): Observable<PolicyAccident> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}`;
    return this.http.get<PolicyAccident>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getAccidentReceipts(policy: PolicyAccident): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts?companyId=${policy.companyId}`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyReceipt[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getAccidentReceipt(
    policy: PolicyAccident,
    receiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts/${receiptNumber}?companyId=${policy.companyId}`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getAccidentPendingReceipts(
    policyNumber: string,
    companyId: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/receiptsPayment/receipts?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getAccidentSinisters(policy: PolicyAccident): Observable<PolicySinister[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicySinister[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getAccidentExpedients(
    policy: PolicyAccident,
    sinisterCode: string
  ): Observable<PolicyExpedient[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters/${sinisterCode}/expedients`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyExpedient[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getAccidentReceiptDocument(
    policyNumber: string,
    companyId: string,
    receiptNumber: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/receipts/${receiptNumber}?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getAccidentParticularConditions(
    policyNumber: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/particularConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getAccidentRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber: homePolicyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }
}
